// Modules
import { useEffect, useState, useMemo, useRef } from "react"
import { useForm, FormProvider } from "react-hook-form"

// Constants
import { STATUS } from "~constants/status"
import { FORM_STEPS } from "~constants/perk-form"
import { ERROR_MESSAGES } from "~constants/errorMessages"
import { PERK_REDEMPTION_TYPE, PERK_REDEMPTION_RULES } from "~constants/perks"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import isCompanyRole from "~rbac/utils/isCompanyRole"
import checkAuthorization from "~rbac/utils/checkAuthorization"

// Context
import { useAuthContext } from "~context/authContext"

// Utils
import { SelectOption, createDropdownCollection } from "~utils/dropdown"
import {
  step1PrefillValues,
  step2PrefillValues,
  step3PrefillValues,
  step5PrefillValues,
  stepCouponListPrefillValues,
  stepExternalLinkPrefillValues,
} from "~components/perks/utils"
import {
  eligibilityRulesToString,
  eligibilityRuleFormParser,
} from "~utils/eligibilityRulesPicker"

// Components
import Modal from "~app/Modal"
import CompletedIcon from "~images/icons/completed.svg?react"
import PerkModalStep1 from "~components/perks/PerkModalStep1"
import PerkModalStep2 from "~components/perks/PerkModalStep2"
import PerkModalStep3 from "~components/perks/PerkModalStep3"
import PerkModalStep4CouponList from "~components/perks/PerkModalStep4CouponList"
import PerkModalStep4ExternalLink from "~components/perks/PerkModalStep4ExternalLink"
import AppSimulator from "~components/app-simulator"
import AppSimulatorModal from "~components/app-simulator/AppSimulatorModal"
import { ErrorMessageSmall } from "~styled/errors"
import { WizardContainer, WizardTitle, WizardForm } from "~styled/wizard"
import {
  ModalSuccessContainer,
  ModalSuccessTitle,
  ModalSuccessText,
} from "~styled/modal"

// Services
import PerkService from "~services/PerkService"
import CouponService from "~services/CouponService"
import CompanyService from "~services/CompanyService"

// Interfaces
import { CompanyShape } from "~shapes/company"
import { OnRequestCloseProps } from "~shapes/modal"
import {
  PerkShape,
  PostPerkShape,
  PerkRedemptionTypeShape,
  PerkRedemptionTypePayload,
} from "~shapes/perk"
import PerkDetailScreen from "~components/app-simulator/PerkDetailScreen"
import PerkModalStep5 from "./PerkModalStep5"
import useSecurities from "~hooks/useSecurities"
import { processImage } from "~utils/utils"

export type PerkData = {
  name: string
  code: string
  description: string
  shortDescription: string
  rules: string
  status: string
  cover: string | null
  logo: string | null
  redeemInstructions: string
  redeemResource: string
  company: SelectOption
  perkType: SelectOption
  ruleLogic: SelectOption
  initialHoldingsRuleLogic: SelectOption
  holdingsMin: string
  holdingsMax: string
  period: SelectOption
  successRedemptionTitle: string
  termsAndConditions: string
  securities: SelectOption[]
}

type CouponListPerkData = {
  uniqueCode: string
  couponsFile: File
  isUniqueCode: boolean
  skipCouponEdit?: boolean
}

interface PerkModalProps {
  onRequestClose: (props: OnRequestCloseProps) => void
  perk?: PerkShape
}

const PerkModal = ({ onRequestClose, perk }: PerkModalProps) => {
  const { loggedCompany } = useAuthContext()

  const [formData, setFormData] = useState<PerkData>()
  const [savedPerk, setSavedPerk] = useState<PerkShape | null>(null)
  const [companies, setCompanies] = useState<CompanyShape[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [perkError, setPerkError] = useState<string>("")
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [lastStepNumber, setLastStepNumber] = useState<number>(5)
  const [perkPreviewData, setPerkPreviewData] = useState<PerkShape | null>(null)
  const [nextButtonLabel, setNextButtonLabel] = useState<string>("Next")
  const [prevButtonLabel, setPrevButtonLabel] = useState<string>("Cancel")
  const [showCancelButton, setShowCancelButton] = useState<boolean>(true)
  const [wizardStepNumber, setWizardStepNumber] = useState<number>(1)
  const [isCompanyEditable, setIsCompanyEditable] = useState<boolean>(false)
  const [showPreviewButton, setShowPreviewButton] = useState<boolean>(false)
  const [isModalScrollable, setIsModalScrollable] = useState<boolean>(false)
  const [isFetchingCompanies, setIsFetchingCompanies] = useState<boolean>(false)
  const [redemptionType, setRedemptionType] = useState<
    PerkRedemptionTypeShape | undefined
  >(undefined)
  const [isFetchingRedemptionType, setIsFetchingRedemptionType] =
    useState<boolean>(false)
  const {
    data: securities,
    isFetching: isFetchingSecurities,
    refetch: fetchSecurities,
  } = useSecurities()

  // Keep the latest securities list in a ref. The perk-prefill effect below is
  // async (it awaits the redemption type before resetting the form), so the
  // securities list can finish loading mid-effect. Reading it from the closure
  // would use a stale value and wipe the securities selection on reset; the ref
  // always holds the freshest list at the moment the reset runs.
  const securitiesRef = useRef(securities)
  securitiesRef.current = securities

  const step1Methods = useForm()
  const step2Methods = useForm()
  const step3Methods = useForm()
  const step4Methods = useForm()
  const step5Methods = useForm()

  const companyRequestParams = { sort: "name", size: 100 }

  // Effect to check user authorizations
  useEffect(() => {
    const isCompanyEditable = checkAuthorization(RBAC_RULES.UPDATE_PERK_COMPANY)
    setIsCompanyEditable(isCompanyEditable)
    ;(async () => {
      await fetchSecurities()
    })()
  }, [])

  // Effect to fetch the companies
  useEffect(() => {
    ;(async () => {
      if (isCompanyEditable) {
        await fetchCompanies()
      }
    })()
  }, [isCompanyEditable])

  // Memoize step3 defaultValues to prevent unnecessary re-renders
  const step3DefaultValues = useMemo(() => {
    const result = perk ? step3PrefillValues(perk) : undefined
    return result
  }, [perk])

  // Effect to fill form with an existing Perk
  useEffect(() => {
    ;(async () => {
      if (perk) {
        const redemptionType = await fetchRedemptionType(perk.id)
        setRedemptionType(redemptionType)

        const step3Values = step3PrefillValues(perk)

        // Prepare securities for reset if we have perkSecurities and securities list.
        // Read the list from the ref so we use the freshest value loaded during the
        // awaits above, not the (possibly stale) value captured when the effect ran.
        const currentSecurities = securitiesRef.current
        let securitiesForReset: SelectOption[] | undefined = undefined
        if (
          step3Values.perkSecurities &&
          step3Values.perkSecurities.length > 0 &&
          currentSecurities &&
          currentSecurities.length > 0
        ) {
          // Convert securities list to dropdown format
          const securitiesItems = currentSecurities.map(k => ({
            id: k.code,
            name: k.exchange + "." + k.symbol,
          }))
          const securitiesDropdown = createDropdownCollection(
            securitiesItems,
            "name",
            "id",
          )

          // Filter to get only the securities that match perkSecurities
          securitiesForReset = securitiesDropdown.filter(option =>
            step3Values.perkSecurities!.includes(option.value as string),
          )
        }

        // Create reset values object that includes securities
        const step3ResetValues = {
          ...step3Values,
          ...(securitiesForReset && securitiesForReset.length > 0
            ? { securities: securitiesForReset }
            : {}),
        }

        // Reset all forms with prefill values (securities are now included in step3ResetValues)
        step1Methods.reset(step1PrefillValues(perk, redemptionType))
        step2Methods.reset(step2PrefillValues(perk))
        step3Methods.reset(step3ResetValues)
        step5Methods.reset(step5PrefillValues(perk))

        await fillCustomStep(perk, redemptionType)
      }
    })()
  }, [perk])

  // Effect to handle Perk creation
  useEffect(() => {
    ;(async () => {
      if (formData) {
        // Create/Save
        if (wizardStepNumber === lastStepNumber) {
          await handlePerkSave(formData)
          return
        }
        setWizardStepNumber(wizardStepNumber + 1)
      }
    })()
  }, [formData])

  // Effect to handle the modal button labels
  useEffect(() => {
    switch (wizardStepNumber) {
      case FORM_STEPS.FIRST:
        setPrevButtonLabel("Cancel")
        setNextButtonLabel("Next")
        setIsModalScrollable(false)
        break

      case lastStepNumber:
        setPrevButtonLabel("Previous")
        setNextButtonLabel(perk ? "Update" : "Create")
        setShowPreviewButton(true)
        setIsModalScrollable(true)
        break

      default:
        setPrevButtonLabel("Previous")
        setNextButtonLabel("Next")
        setShowPreviewButton(false)
        setIsModalScrollable(true)
        break
    }
  }, [wizardStepNumber])

  // Effect to change the UI if the update/create was success
  useEffect(() => {
    if (isSuccess) {
      setNextButtonLabel("Done")
      setShowCancelButton(false)
    }
  }, [isSuccess])

  const fetchCompanies = async () => {
    setIsFetchingCompanies(true)

    try {
      const response = await CompanyService.getCompanies(companyRequestParams)
      setCompanies(response._embedded.companies)
    } catch (err) {
      setCompanies([])
    }

    setIsFetchingCompanies(false)
  }

  const fetchRedemptionType = async (perkId: string) => {
    setIsFetchingRedemptionType(true)

    const response = await PerkService.getPerkRedemptionType(perkId)
    const redemptionType: PerkRedemptionTypeShape =
      response._embedded["perk-redemption-types"][0]

    setIsFetchingRedemptionType(false)
    return redemptionType
  }

  const fillCustomStep = async (
    perk: PerkShape,
    redemptionType: PerkRedemptionTypeShape,
  ) => {
    switch (redemptionType.type) {
      case PERK_REDEMPTION_TYPE.ExternalLink:
        step4Methods.reset(stepExternalLinkPrefillValues(perk))
        break

      case PERK_REDEMPTION_TYPE.CouponList:
        step4Methods.reset(
          stepCouponListPrefillValues(
            await PerkService.getPerkRedemptionTypeCoupons(redemptionType.id),
          ),
        )
        break
    }
  }

  const handlePerkSave = async (formData: PerkData) => {
    setIsLoading(true)
    try {
      const updatedPerk = await createOrUpdatePerk(formData)
      const type =
        redemptionType ?? (await createRedemptionType(formData, updatedPerk))

      await couponListTypeHandler(
        formData as PerkData & CouponListPerkData,
        type,
      )

      setSavedPerk(updatedPerk)
      setPerkError("")
      setIsLoading(false)
      setIsSuccess(true)
    } catch (err) {
      setPerkError(err.message)
      setIsLoading(false)
    }
  }

  const createOrUpdatePerk = async (data: PerkData): Promise<PerkShape> => {
    try {
      let company: CompanyShape | null = loggedCompany
      let coverImageUrl = ""
      let logoImageUrl = ""

      if (!isCompanyRole()) {
        company = companies.find(el => el.id === data.company?.value) || null
      }

      // Cannot proceed if we do not have a company
      if (!company) {
        setPerkError(ERROR_MESSAGES.REQUIRED_COMPANY)
        return null!
      }

      const eligibilityRules = eligibilityRulesToString(
        eligibilityRuleFormParser(data),
      )

      // Upload cover only if it's not a path
      coverImageUrl = await processImage("perk", data.cover, "cover", data.code)
      logoImageUrl = await processImage("perk", data.logo, "logo", data.code)

      const perkPayload: PostPerkShape = {
        name: data.name,
        code: data.code,
        description: data.description,
        shortDescription: data.shortDescription,
        rules: data.rules,
        cover: coverImageUrl,
        logo: logoImageUrl,
        eligibilityRules,
        redeemInstructions: data.redeemInstructions,
        redeemResource: data.redeemResource,
        status: perk ? perk.status : STATUS.INACTIVE,
        company: company?._links?.self.href || "",
        successRedemptionTitle: data.successRedemptionTitle,
        termsAndConditions: data.termsAndConditions
          ? data.termsAndConditions.trim()
          : "",
      }

      // Add perk id if we are updating
      if (perk?.id) {
        perkPayload.id = perk.id
        // Add attributes
      } else {
        perkPayload.attributes = {
          redemptionRules: [
            {
              rule: PERK_REDEMPTION_RULES.PERKS_NOT_ALREADY_REDEEMED,
              perks: [{ id: "myself" }],
            },
          ],
        }
      }

      let response: PerkShape = null!

      if (perk) response = await PerkService.patchPerk(perkPayload)
      else response = await PerkService.postPerk(perkPayload)

      return response
    } catch (err) {
      setPerkError(err.message)
      setIsLoading(false)
      throw err
    }
  }

  const createRedemptionType = async (data: PerkData, newPerk: PerkShape) => {
    try {
      if (!perk && !!newPerk) {
        const payload: PerkRedemptionTypePayload = {
          perk: newPerk._links!.self.href,
          type: data.perkType.value,
        }
        return await PerkService.postPerkRedemptionType(payload)
      }
    } catch (err) {
      setPerkError(err.message)
      setIsLoading(false)
      throw err
    }
  }

  const couponListTypeHandler = async (
    data: PerkData & CouponListPerkData,
    redemptionType: PerkRedemptionTypeShape,
  ) => {
    try {
      if (redemptionType?.type === PERK_REDEMPTION_TYPE.CouponList) {
        // When editing, the user can choose to keep the existing coupon codes
        // untouched. In that case we skip any coupon creation/upload entirely.
        if (data.skipCouponEdit) return

        // Create manually the code
        if (data.isUniqueCode) {
          await CouponService.postCouponsBatch({
            content: [
              {
                code: data.uniqueCode,
                general: true,
                redemptionType: redemptionType._links!.self!.href,
              },
            ],
          })
        }
        // Upload the CSV
        else {
          await CouponService.postCouponsByCSV(
            data.couponsFile,
            redemptionType._links!.self!.href,
          )
        }
      }
    } catch (err) {
      setPerkError(err.message)
      setIsLoading(false)
      throw err
    }
  }

  const onFormSubmit = (data: PerkData) => {
    setFormData({ ...formData, ...data })
  }

  const onMoveStepBack = () => {
    const step = wizardStepNumber - 1

    setWizardStepNumber(step)
    if (step < FORM_STEPS.FIRST) {
      onRequestClose({ shouldRefreshList: false })
    }
  }

  const onMoveStepForward = () => {
    if (isSuccess) {
      // Surface the API response (savedPerk) so parents like PerkDetail
      // re-render with the just-persisted values instead of the prop snapshot
      // we received before editing.
      onRequestClose({ shouldRefreshList: true, data: savedPerk ?? perk })
      return
    }

    switch (wizardStepNumber) {
      case FORM_STEPS.FIRST:
        step1Methods.handleSubmit(onFormSubmit)()
        break

      case FORM_STEPS.SECOND:
        step2Methods.handleSubmit(onFormSubmit)()
        break

      case FORM_STEPS.THIRD:
        step3Methods.handleSubmit(onFormSubmit)()
        break

      case FORM_STEPS.FOURTH:
        step4Methods.handleSubmit(onFormSubmit)()
        break

      case FORM_STEPS.FIFTH:
        step5Methods.handleSubmit(onFormSubmit)()
        break
    }
  }

  const onPreviewRequested = () => {
    setShowPreview(true)

    if (perk) {
      setPerkPreviewData(perk)
      return
    }

    setPerkPreviewData(getPerkShapeFromWizard())
  }

  const showRedemptionWorkflow = (redemptionType?: string) =>
    redemptionType !== PERK_REDEMPTION_TYPE.ExternalLink &&
    redemptionType !== PERK_REDEMPTION_TYPE.CouponList

  const getPerkShapeFromWizard = (): PerkShape => {
    let company: CompanyShape | null = loggedCompany

    if (!isCompanyRole()) {
      company = companies.find(el => el.id === formData!.company?.value)!
    }

    const eligibilityRules = eligibilityRulesToString(
      eligibilityRuleFormParser(formData!),
    )
    const fakeDate = new Date().toDateString()

    return {
      id: null!,
      name: formData!.name,
      description: formData!.description,
      shortDescription: formData!.shortDescription,
      rules: formData!.rules,
      eligibilityRules,
      status: STATUS.INACTIVE,
      cover: formData!.cover,
      logo: formData!.logo,
      redeemInstructions: formData!.redeemInstructions,
      redeemResource: formData!.redeemResource,
      termsAndConditions: formData!.termsAndConditions,
      createdAt: fakeDate,
      updatedAt: fakeDate,
      company,
      successRedemptionTitle: formData!.successRedemptionTitle,
    }
  }

  return (
    <>
      <Modal
        isOpen={true}
        scrollable={isModalScrollable}
        buttonLabel={nextButtonLabel}
        cancelLabel={prevButtonLabel}
        cancelButton={showCancelButton}
        optionalButtonLabel="Preview"
        isButtonLoading={
          isLoading ||
          isFetchingCompanies ||
          isFetchingRedemptionType ||
          isFetchingSecurities
        }
        onRequestClose={() => onRequestClose({ shouldRefreshList: false })}
        onRequestCancel={onMoveStepBack}
        onRequestConfirm={onMoveStepForward}
        showOptionalButton={showPreviewButton}
        onOptionalButtonClicked={onPreviewRequested}
      >
        <WizardContainer>
          {!isSuccess && (
            <WizardTitle>
              {perk ? "Edit a " : "Create New"} Perk - Step {wizardStepNumber}/
              {lastStepNumber}
            </WizardTitle>
          )}

          <WizardForm>
            {/* STEP 1 */}
            <FormProvider {...step1Methods}>
              <PerkModalStep1
                isVisible={!isSuccess && wizardStepNumber === FORM_STEPS.FIRST}
                companies={companies}
                isLoading={
                  isLoading || isFetchingCompanies || isFetchingRedemptionType
                }
                perkTypeDisabled={!!perk}
              />
            </FormProvider>

            {/* STEP 2 */}
            <FormProvider {...step2Methods}>
              <PerkModalStep2
                isVisible={!isSuccess && wizardStepNumber === FORM_STEPS.SECOND}
              />
            </FormProvider>

            {/* STEP 3 */}
            {!!securities && (
              <FormProvider {...step3Methods}>
                <PerkModalStep3
                  isVisible={
                    !isSuccess && wizardStepNumber === FORM_STEPS.THIRD
                  }
                  defaultValues={step3DefaultValues}
                  securitiesList={securities}
                />
              </FormProvider>
            )}

            {/* STEP 4 */}
            <FormProvider {...step4Methods}>
              {formData?.perkType.value === PERK_REDEMPTION_TYPE.CouponList && (
                <PerkModalStep4CouponList
                  disabled={false}
                  isEditMode={!!perk}
                  isVisible={
                    !isSuccess && wizardStepNumber === FORM_STEPS.FOURTH
                  }
                />
              )}

              {formData?.perkType.value ===
                PERK_REDEMPTION_TYPE.ExternalLink && (
                <PerkModalStep4ExternalLink
                  isVisible={
                    !isSuccess && wizardStepNumber === FORM_STEPS.FOURTH
                  }
                />
              )}
              {showRedemptionWorkflow(formData?.perkType.value) &&
                wizardStepNumber === FORM_STEPS.FOURTH && (
                  <div>
                    Redemption Workflow is all set. Press Next to continue
                  </div>
                )}
            </FormProvider>

            {/* STEP 5 */}
            <FormProvider {...step5Methods}>
              <PerkModalStep5
                isVisible={!isSuccess && wizardStepNumber === FORM_STEPS.FIFTH}
              />
            </FormProvider>

            {/* SUCCESS */}
            {isSuccess && (
              <ModalSuccessContainer>
                <CompletedIcon />

                <ModalSuccessTitle>
                  Perk succesfully {!perk ? "created" : "edited"}
                </ModalSuccessTitle>

                {!perk && (
                  <ModalSuccessText>
                    The perk will be created as inactive. To see it in
                    production activate it form the row options.
                  </ModalSuccessText>
                )}
              </ModalSuccessContainer>
            )}
          </WizardForm>
        </WizardContainer>

        <ErrorMessageSmall style={{ textAlign: "right" }}>
          {perkError}
        </ErrorMessageSmall>
      </Modal>

      {showPreview && !!perkPreviewData && (
        <AppSimulatorModal
          isOpen={showPreview}
          onRequestClose={() => setShowPreview(false)}
        >
          <AppSimulator>
            <PerkDetailScreen perk={perkPreviewData} />
          </AppSimulator>
        </AppSimulatorModal>
      )}
    </>
  )
}

export default PerkModal
